<template>
  <div class="py-16 bg-gray-50 min-h-[calc(100vh-200px)]">
    <div class="container mx-auto px-4">
      <h2 class="text-3xl font-bold text-center text-gray-800">
        Consulta de Situação Eleitoral
      </h2>
      <div class="text-lg text-center text-gray-600 mb-10 mt-2">
        Verifique se você está apto a votar nas eleições.
      </div>

      <div class="flex justify-center">
        <div class="w-full max-w-lg">
          <div class="bg-white shadow-md rounded-lg p-8">
            <form @submit.prevent="consultar">
              <p class="mb-6 text-center text-gray-700">
                Informe o seu número de inscrição (ex: CPF, CRM, OAB) para
                consultar a sua situação.
              </p>

              <div class="mb-6">
                <label
                  class="block text-gray-700 text-sm font-bold mb-2"
                  for="inscricao"
                >
                  Número de Inscrição
                </label>
                <input
                  id="inscricao"
                  v-model.trim="inscricao"
                  type="text"
                  placeholder="Informe seu documento"
                  class="
                    shadow
                    appearance-none
                    border
                    rounded
                    w-full
                    py-3
                    px-3
                    text-gray-700
                    leading-tight
                    focus:outline-none focus:shadow-outline
                  "
                  required
                />
              </div>

              <!-- ReCAPTCHA widget container -->
              <div class="mb-6 flex justify-center">
                <div id="recaptcha-situacao">
                  <span class="text-sm text-gray-500"
                    >Carregando reCAPTCHA...</span
                  >
                </div>
              </div>

              <div class="text-center mt-6">
                <Button
                  type="submit"
                  color="white"
                  background="lime-600"
                  :disabled="loading || !inscricao || !captchaToken"
                >
                  <span v-if="loading">Consultando...</span>
                  <span v-else>Consultar Situação</span>
                </Button>
              </div>
            </form>

            <div v-if="resultado" class="mt-8">
              <div
                :class="[
                  'border-l-4 p-4 rounded shadow-sm',
                  resultado.status === 'APTO'
                    ? 'border-green-500 bg-green-50 text-green-900'
                    : resultado.status === 'NAO_APTO'
                    ? 'border-yellow-500 bg-yellow-50 text-yellow-900'
                    : 'border-red-500 bg-red-50 text-red-900',
                ]"
              >
                <h3 class="text-lg font-bold mb-2">
                  {{
                    resultado.status === "APTO"
                      ? "APTO PARA VOTAR"
                      : resultado.status === "NAO_APTO"
                      ? "NÃO APTO"
                      : "NÃO ENCONTRADO"
                  }}
                </h3>
                <div v-if="resultado.nome" class="mb-2">
                  <strong>Profissional:</strong> {{ resultado.nome }}
                </div>
                <p class="mb-0">{{ resultado.mensagem }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import Api from "@/api";
import { mapGetters } from "vuex";
import Button from "@/components/elements/Button.vue";

export default Vue.extend({
  name: "ConsultaSituacaoView",
  components: {
    Button,
  },
  computed: {
    ...mapGetters("eleicaonet", ["getParamHotsite", "getInfo"]),
    configuracaoTextos() {
      return this.getParamHotsite?.home || {};
    },
  },
  data() {
    return {
      inscricao: "",
      loading: false,
      resultado: null,
      captchaToken: "",
      captchaWidgetId: null,
    };
  },
  mounted() {
    this.carregarRecaptcha();
  },
  methods: {
    carregarRecaptcha() {
      // Cria a função de callback global se não existir
      window.onRecaptchaSuccessSituacao = (token) => {
        this.captchaToken = token;
      };

      // Define callback pro carregamento do script (se for o primeiro a carregar)
      window.onRecaptchaLoadSituacao = () => {
        this.inicializarRecaptcha();
      };

      if (window.grecaptcha && window.grecaptcha.render) {
        this.inicializarRecaptcha();
      } else {
        const scriptJaExiste = document.querySelector(
          'script[src*="recaptcha/api.js"]'
        );
        if (!scriptJaExiste) {
          const script = document.createElement("script");
          script.src =
            "https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoadSituacao&render=explicit&hl=pt-BR";
          script.async = true;
          script.defer = true;
          document.head.appendChild(script);
        } else {
          // Se o script já existe mas o grecaptcha não tá pronto, aguarda
          const interval = setInterval(() => {
            if (window.grecaptcha && window.grecaptcha.render) {
              clearInterval(interval);
              this.inicializarRecaptcha();
            }
          }, 500);
        }
      }
    },
    inicializarRecaptcha() {
      const container = document.getElementById("recaptcha-situacao");
      if (container && window.grecaptcha && window.grecaptcha.render) {
        container.innerHTML = "";
        try {
          this.captchaWidgetId = window.grecaptcha.render(
            "recaptcha-situacao",
            {
              sitekey: "6LfBJxYrAAAAABKOrs-rPGQKdP1DoB0GSzZpYcBK",
              callback: "onRecaptchaSuccessSituacao",
            }
          );
        } catch (e) {
          console.warn("ReCAPTCHA já renderizado no container", e);
        }
      }
    },
    async consultar() {
      if (!this.inscricao || !this.captchaToken) return;

      this.loading = true;
      this.resultado = null;

      try {
        const response = await Api.get("/Eleitores/situacao-eleitoral", {
          params: {
            inscricao: this.inscricao,
            captcha: this.captchaToken,
          },
        });

        this.resultado = response.data;
        this.resultado.mensagem = this.formatarMensagem(this.resultado);
      } catch (error) {
        console.error("Erro ao consultar situação", error);
        this.resultado = {
          status: "ERRO",
          mensagem: this.formatarMensagem(
            { status: "ERRO" },
            error.response?.data?.error?.message
          ),
        };
      } finally {
        this.loading = false;
        // Reseta o captcha pra próxima consulta
        if (window.grecaptcha && this.captchaWidgetId !== null) {
          window.grecaptcha.reset(this.captchaWidgetId);
          this.captchaToken = "";
        }
      }
    },
    formatarMensagem(res, fallbackError = null) {
      let textoBase = "";
      const { status, nome } = res;

      switch (status) {
        case "APTO":
          textoBase =
            this.configuracaoTextos.msgApto ||
            "Profissional apto para votação.";
          break;
        case "NAO_APTO":
          textoBase =
            this.configuracaoTextos.msgInapto ||
            "Profissional consta com impedimento para votação.";
          break;
        case "NAO_ENCONTRADO":
          textoBase =
            this.configuracaoTextos.msgNaoEncontrado ||
            "Profissional não encontrado na base de dados.";
          break;
        default:
          textoBase =
            this.configuracaoTextos.msgErro ||
            fallbackError ||
            "Ocorreu um erro ao realizar a consulta. Tente novamente mais tarde.";
          break;
      }

      // Substitui variáveis
      let entidade = this.getInfo?.entidadeEleicao || "";
      let textoFormatado = textoBase
        .replace(/<varivale_regional>/gi, entidade)
        .replace(/<regional>/gi, entidade)
        .replace(/<situacao>/gi, status === "NAO_APTO" ? "INAPTO" : status)
        .replace(/<nome>/gi, nome || "");

      return textoFormatado;
    },
  },
});
</script>
